import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

interface DailyPerformance {
  date: string;
  total: number;
  correct: number;
  accuracy: number;
}

interface DisciplinePerformance {
  name: string;
  total: number;
  correct: number;
  accuracy: number;
  color: string;
}

interface PerformanceChartProps {
  dailyData: DailyPerformance[];
  disciplineData: DisciplinePerformance[];
}

const chartConfig = {
  accuracy: {
    label: "Taxa de Acerto",
    color: "hsl(var(--primary))",
  },
  total: {
    label: "Total",
    color: "hsl(var(--muted-foreground))",
  },
  correct: {
    label: "Corretas",
    color: "hsl(var(--primary))",
  },
};

export function PerformanceLineChart({ data }: { data: DailyPerformance[] }) {
  const chartData = useMemo(() => {
    return data.map(d => ({
      ...d,
      date: new Date(d.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
    }));
  }, [data]);

  if (data.length === 0) {
    return (
      <Card className="h-full flex flex-col bg-green-500 border-green-600">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-white">Evolução do Desempenho</CardTitle>
          <CardDescription className="text-white/80">Taxa de acerto ao longo do tempo</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <p className="text-sm text-white/80">Sem dados suficientes</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col bg-green-500 border-green-600">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-white">Evolução do Desempenho</CardTitle>
        <CardDescription className="text-white/80">Taxa de acerto ao longo do tempo</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pt-0 h-[180px] sm:h-[200px] md:h-[220px]">
        <ChartContainer config={chartConfig} className="h-full w-full [&_.recharts-cartesian-axis-tick_text]:fill-white">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#ffffff' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9, fill: '#ffffff' }} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={30} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line 
                type="monotone" 
                dataKey="accuracy" 
                stroke="#ffffff" 
                strokeWidth={2}
                dot={{ fill: "#ffffff", strokeWidth: 2, stroke: "#ffffff" }}
                activeDot={{ r: 6, fill: "#ffffff" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

export function DisciplineBarChart({ data }: { data: DisciplinePerformance[] }) {
  const chartData = useMemo(() => {
    return data.slice(0, 6).map(d => ({
      name: d.name.length > 12 ? d.name.slice(0, 12) + '...' : d.name,
      fullName: d.name,
      accuracy: d.accuracy,
      total: d.total,
      color: d.color,
    }));
  }, [data]);

  if (data.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Desempenho por Disciplina</CardTitle>
          <CardDescription>Taxa de acerto em cada matéria</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Sem dados suficientes</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Desempenho por Disciplina</CardTitle>
        <CardDescription>Taxa de acerto em cada matéria</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pt-0 h-[180px] sm:h-[200px] md:h-[220px]">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <XAxis dataKey="name" tick={{ fontSize: 8 }} tickLine={false} axisLine={false} angle={-15} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={30} />
              <ChartTooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <p className="font-medium">{data.fullName}</p>
                        <p className="text-sm text-muted-foreground">Taxa: {data.accuracy}%</p>
                        <p className="text-sm text-muted-foreground">Questões: {data.total}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="accuracy" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color || "hsl(var(--primary))"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

export function DisciplineDistributionChart({ data }: { data: DisciplinePerformance[] }) {
  const chartData = useMemo(() => {
    const top5 = data.slice(0, 5);
    const others = data.slice(5);
    const result = top5.map(d => ({ name: d.name, value: d.total, color: d.color }));
    if (others.length > 0) {
      result.push({
        name: 'Outras',
        value: others.reduce((sum, d) => sum + d.total, 0),
        color: '#94a3b8',
      });
    }
    return result;
  }, [data]);

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Distribuição de Questões</CardTitle>
          <CardDescription>Quantidade por disciplina</CardDescription>
        </CardHeader>
        <CardContent className="flex h-[200px] items-center justify-center">
          <p className="text-sm text-muted-foreground">Sem dados suficientes</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Distribuição de Questões</CardTitle>
        <CardDescription>Quantidade por disciplina</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-center">
          <ChartContainer config={chartConfig} className="h-[180px] w-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <ChartTooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="rounded-lg border bg-background p-2 shadow-sm">
                          <p className="font-medium">{data.name}</p>
                          <p className="text-sm text-muted-foreground">{data.value} questões</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
          <div className="ml-4 space-y-1">
            {chartData.slice(0, 5).map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: d.color }} />
                <span className="truncate max-w-[100px]">{d.name}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
